<template>
  <div class="structure-page">
    <div class="editor-panel">
      <div class="panel-header">
        <div class="panel-title">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"/>
          </svg>
          <span>文本编辑器</span>
        </div>
        <button class="btn-history" @click="showSavedArchitectures">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          历史功能
        </button>
      </div>

      <div class="draw-mode-tabs">
        <div
          :class="['tab-item', { active: drawMode === 'role' }]"
          @click="drawMode = 'role'">
          角色视图绘制
        </div>
        <div
          :class="['tab-item', { active: drawMode === 'function' }]"
          @click="drawMode = 'function'">
          模块视图绘制
        </div>
      </div>

      <div class="tree-content">
        <el-tree
            :data="treeData"
            :props="defaultProps"
            :expand-on-click-node="false"
            default-expand-all
            :show-checkbox="false"
            node-key="id"
            class="custom-tree"
            @node-click="handleNodeClick">
          <span class="custom-tree-node" slot-scope="{ node, data }">
            <span v-if="!data.editing" @dblclick="startEdit(data)">
              {{ node.label }}
            </span>
            <el-input
                v-else
                v-model="data.tempLabel"
                size="mini"
                class="edit-input"
                @blur="saveEdit(data)"
                @keyup.enter="saveEdit(data)"
                @keyup.esc="cancelEdit(data)"
                ref="editInput">
            </el-input>
            <span class="node-actions">
              <el-button
                  type="text"
                  size="mini"
                  @click.stop="startEdit(data)"
                  title="编辑">
                <i class="el-icon-edit"></i>
              </el-button>
              <el-button
                  v-if="data.level < 4"
                  type="text"
                  size="mini"
                  @click.stop="append(data)"
                  title="添加子模块">
                <i class="el-icon-plus"></i>
              </el-button>
              <el-button
                  type="text"
                  size="mini"
                  @click.stop="remove(node, data)"
                  title="删除">
                <i class="el-icon-delete"></i>
              </el-button>
            </span>
          </span>
        </el-tree>
      </div>

      <div class="panel-footer">
        <button class="btn-ai" @click="generateAIDiagram">
          <span class="btn-content">
            <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
            AI功能图
          </span>
          <span class="btn-points">（消耗{{ pointsConfig.structureChart }}积分）</span>
        </button>
      </div>
    </div>

    <div class="preview-panel">
      <div class="tabs-container">
        <div class="tabs">
          <button class="tab active">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"/></svg>
            框架图预览
          </button>
        </div>
        <div class="toolbar-buttons">
          <button class="tool-btn btn-green" @click="zoomIn" title="放大">
            <i class="el-icon-zoom-in"></i>放大
          </button>
          <button class="tool-btn btn-orange" @click="zoomOut" title="缩小">
            <i class="el-icon-zoom-out"></i>缩小
          </button>
          <button class="tool-btn btn-blue" @click="openExportDialog" title="导出功能图">
            <i class="el-icon-download"></i>导出功能图
          </button>
          <button class="tool-btn btn-purple" @click="toggleFullscreen" :title="isFullscreen ? '退出全屏' : '全屏'">
            <i class="el-icon-full-screen"></i>{{ isFullscreen ? '退出全屏' : '全屏' }}
          </button>
        </div>
      </div>

      <div class="preview-content">
        <div class="preview-area">
          <div class="toolbar">
            <div class="tip-banner">
              💡 提示：双击可以修改文字，当功能数量较多时，建议：点击全屏 → 缩小显示 → 使用截图工具保存
            </div>
          </div>

          <div class="params-setting">
            <div class="param-item">
              <label>字体：</label>
              <el-select v-model="fontFamily" size="small" style="width: 140px;" @change="updateFont">
                <el-option label="宋体" value="SimSun"></el-option>
                <el-option label="黑体" value="SimHei"></el-option>
                <el-option label="微软雅黑" value="Microsoft YaHei"></el-option>
                <el-option label="仿宋" value="FangSong"></el-option>
                <el-option label="楷体" value="KaiTi"></el-option>
                <el-option label="楷体" value="KaiTi_GB2312"></el-option>
                <el-option label="Times New Roman" value="Times New Roman"></el-option>
                <el-option label="Arial" value="Arial"></el-option>
              </el-select>
            </div>
            <div class="param-item">
              <label>字体大小：</label>
              <el-select v-model="fontSize" size="small" style="width: 90px;" @change="updateFont">
                <el-option label="12px" value="12"></el-option>
                <el-option label="13px" value="13"></el-option>
                <el-option label="14px" value="14"></el-option>
                <el-option label="16px" value="16"></el-option>
                <el-option label="18px" value="18"></el-option>
              </el-select>
            </div>
            <div class="param-item">
              <label>连接线：</label>
              <el-select v-model="lineType" size="small" style="width: 90px;" @change="updateFont">
                <el-option label="直线" value="line"></el-option>
                <el-option label="箭头" value="arrow"></el-option>
              </el-select>
            </div>
            <div class="param-item">
              <label>矩形高度：</label>
              <el-input-number
                  v-model="rectHeight"
                  size="small"
                  :min="50"
                  :max="300"
                  :step="5"
                  style="width: 130px;"
                  @change="updateFont">
              </el-input-number>
            </div>
            <div class="param-item">
              <label>层级间距：</label>
              <el-input-number
                  v-model="levelSpacing"
                  size="small"
                  :min="50"
                  :max="200"
                  :step="10"
                  style="width: 130px;"
                  @change="updateFont">
              </el-input-number>
            </div>

          </div>

          <div class="diagram-content">
            <!-- 编辑输入框 -->
            <input
              v-if="editingNode"
              ref="editInput"
              v-model="editingText"
              :style="editInputStyle"
              class="node-edit-input"
              @blur="saveEditNode"
              @keyup.enter="saveEditNode"
              @keyup.esc="cancelEditNode"
            />

            <svg
              class="diagram-svg"
              :viewBox="zoomedViewBox"
              preserveAspectRatio="xMidYMid meet"
              :key="svgKey"
              @mousedown="startPan"
              @mousemove="onDrag"
              @mouseup="endDrag"
              @mouseleave="endDrag"
              :class="{ 'panning': isPanning }">
            <!-- 箭头标记定义 -->
            <defs v-if="lineType === 'arrow'">
              <marker id="arrowhead" markerWidth="10" markerHeight="7"
                      refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="black"/>
              </marker>
            </defs>

            <!-- 连接线 -->
            <g class="connections">
              <path
                  v-for="line in connectionLines"
                  :key="line.id"
                  :d="line.path"
                  stroke="black"
                  stroke-width="1"
                  fill="none"
                  :marker-end="lineType === 'arrow' && line.isToChild ? 'url(#arrowhead)' : ''">
              </path>
            </g>

            <!-- 节点 -->
            <g class="nodes">
              <g v-for="node in allVisibleNodes" :key="node.id">
                <g @mouseover="handleNodeMouseOver(node)"
                   @mouseout="handleNodeMouseOut">
                  <rect
                      :x="node.x"
                      :y="node.y"
                      :width="node.width"
                      :height="node.height"
                      stroke="black"
                      stroke-width="1"
                      fill="white"
                      @mousedown="startDrag($event, node)"
                      @dblclick="startEditNode($event, node)"
                      class="draggable-node">
                  </rect>

                  <!-- 第1级节点：水平文字 -->
                  <text
                      v-if="node.level === 1"
                      :x="node.x + node.width/2"
                      :y="node.y + node.height/2 + parseInt(fontSize) * 0.1"
                      text-anchor="middle"
                      dominant-baseline="central"
                      :font-size="fontSize"
                      :font-family="fontFamily"
                      fill="black">
                    {{ node.label }}
                  </text>

                  <!-- 2、3、4级节点：竖直文字 -->
                  <g v-else>
                    <text
                        v-for="(char, index) in node.label"
                        :key="index"
                        :x="node.x + node.width/2"
                        :y="node.y + node.height/2 - (node.label.length - 1) * (parseInt(fontSize) * 0.65) + index * (parseInt(fontSize) * 1.3)"
                        text-anchor="middle"
                        dominant-baseline="central"
                        :font-size="fontSize"
                        :font-family="fontFamily"
                        fill="black">
                      {{ char }}
                    </text>
                  </g>

                  <!-- level=2、3、4 节点的按钮 -->
                  <g v-if="(node.level === 2 || node.level === 3 || node.level === 4) && hoveredNodeId === node.id">
                    <!-- 左侧 + 按钮 -->
                    <g @click.stop="addSiblingNode(node, 'left')"
                       @mouseover="handleButtonMouseOver(node)"
                       @mouseout="handleNodeMouseOut"
                       class="add-button">
                      <circle
                          :cx="node.x - 10"
                          :cy="node.y + node.height/2"
                          r="8"
                          fill="#10b981"
                          stroke="white"
                          stroke-width="1.5"
                          class="add-btn-circle">
                      </circle>
                      <text
                          :x="node.x - 10"
                          :y="node.y + node.height/2"
                          dy="0.35em"
                          text-anchor="middle"
                          font-size="16"
                          font-weight="bold"
                          fill="white"
                          class="add-btn-text">
                        +
                      </text>
                    </g>

                    <!-- 右侧 + 按钮 -->
                    <g @click.stop="addSiblingNode(node, 'right')"
                       @mouseover="handleButtonMouseOver(node)"
                       @mouseout="handleNodeMouseOut"
                       class="add-button">
                      <circle
                          :cx="node.x + node.width + 10"
                          :cy="node.y + node.height/2"
                          r="8"
                          fill="#10b981"
                          stroke="white"
                          stroke-width="1.5"
                          class="add-btn-circle">
                      </circle>
                      <text
                          :x="node.x + node.width + 10"
                          :y="node.y + node.height/2"
                          dy="0.35em"
                          text-anchor="middle"
                          font-size="16"
                          font-weight="bold"
                          fill="white"
                          class="add-btn-text">
                        +
                      </text>
                    </g>

                    <!-- 底部 + 按钮（只在 level=2 和 level=3 时显示） -->
                    <g v-if="node.level === 2 || node.level === 3"
                       @click.stop="addChildNode(node)"
                       @mouseover="handleButtonMouseOver(node)"
                       @mouseout="handleNodeMouseOut"
                       class="add-button">
                      <circle
                          :cx="node.x + node.width/2"
                          :cy="node.y + node.height + 10"
                          r="8"
                          fill="#10b981"
                          stroke="white"
                          stroke-width="1.5"
                          class="add-btn-circle">
                      </circle>
                      <text
                          :x="node.x + node.width/2"
                          :y="node.y + node.height + 10"
                          dy="0.35em"
                          text-anchor="middle"
                          font-size="16"
                          font-weight="bold"
                          fill="white"
                          class="add-btn-text">
                        +
                      </text>
                    </g>

                    <!-- 右上角删除按钮 -->
                    <g @click.stop="deleteNodeFromSVG(node)"
                       @mouseover="handleButtonMouseOver(node)"
                       @mouseout="handleNodeMouseOut"
                       class="delete-button">
                      <circle
                          :cx="node.x + node.width + 6"
                          :cy="node.y - 6"
                          r="8"
                          fill="#ef4444"
                          stroke="white"
                          stroke-width="1.5"
                          class="delete-btn-circle">
                      </circle>
                      <text
                          :x="node.x + node.width + 6"
                          :y="node.y - 6"
                          dy="0.35em"
                          text-anchor="middle"
                          font-size="18"
                          font-weight="bold"
                          fill="white"
                          class="delete-btn-text">
                        ×
                      </text>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史功能模态框 -->
    <el-dialog
        title="历史功能列表"
        :visible.sync="showHistoryDialog"
        width="1000px"
        :close-on-click-modal="false">
      <div v-if="historyArchitectures.length === 0" style="text-align: center; padding: 20px; color: #999;">
        暂无保存的功能
      </div>
      <div v-else>
        <div style="margin-bottom: 16px; padding: 15px; background-color: #f8f9fa; border-radius: 4px; font-size: 13px; color: #666;">
          <p><strong>说明：</strong></p>
          <p>• 共有 {{ totalRecords }} 个已保存的功能（按保存时间倒序排列）</p>
          <p>• 点击"加载"按钮可以加载对应的功能</p>
          <p>• 系统最多保存20条历史功能记录</p>
        </div>
        <el-table
            :data="historyArchitectures"
            stripe
            style="width: 100%"
            max-height="400">
          <el-table-column
              prop="index"
              label="序号"
              width="60"
              align="center">
          </el-table-column>
          <el-table-column
              prop="projectName"
              label="课题名称"
              min-width="150"
              align="center">
          </el-table-column>
          <el-table-column
              prop="viewTypeName"
              label="视图类型"
              width="100"
              align="center">
          </el-table-column>
          <el-table-column
              label="系统项目描述"
              width="120"
              align="center">
            <template slot-scope="{ row }">
              <el-button v-if="row.projectDescription" type="text" size="mini" @click="viewProjectDesc(row.projectDescription)">查看</el-button>
              <span v-else style="color: #999;">暂无描述</span>
            </template>
          </el-table-column>
          <el-table-column
              prop="nodeCount"
              label="节点数"
              width="80"
              align="center">
            <template slot-scope="scope">
              <span style="color: #409EFF; font-weight: bold;">{{ scope.row.nodeCount }}</span>
            </template>
          </el-table-column>
          <el-table-column
              prop="createTime"
              label="保存时间"
              width="160"
              align="center">
          </el-table-column>
          <el-table-column
              label="操作"
              width="160"
              align="center">
            <template slot-scope="scope">
              <el-button
                  type="primary"
                  size="mini"
                  @click="loadHistoryArchitecture(scope.row)">
                加载
              </el-button>
              <el-button
                  type="danger"
                  size="mini"
                  @click="deleteHistoryArchitecture(scope.row)">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page.sync="currentPage"
            :page-size="pageSize"
            :total="totalRecords"
            @current-change="handlePageChange"
            layout="prev, pager, next" />
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showHistoryDialog = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- AI生成功能图模态框 -->
    <el-dialog
        title="AI生成功能图"
        :visible.sync="showAIDialog"
        width="600px"
        :close-on-click-modal="false">
      <el-form :model="aiForm" label-width="120px">
        <el-form-item label="课题名称">
          <el-input
              v-model="aiForm.projectName"
              placeholder="例如：川剧文化传播平台"
              clearable>
          </el-input>
        </el-form-item>

        <el-form-item label="视图类型">
          <el-radio-group v-model="aiForm.viewType">
            <el-radio label="role">按角色视图绘制</el-radio>
            <el-radio label="function">按功能模块绘制</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="系统项目描述">
          <el-input
              v-model="aiForm.projectDescription"
              type="textarea"
              :rows="8"
              :placeholder="aiForm.viewType === 'role' ?
                '请描述系统的角色和功能，例如：\n\n用户端功能：\n- 登录注册：用户注册账号或使用已有账号登录系统\n- 首页：展示网站核心内容，包括轮播图、推荐内容等\n- 川剧介绍：详细介绍川剧的历史渊源、艺术流派等\n\n管理端功能：\n- 登录：管理员通过账号和密码登录后台管理系统\n- 数据统计：展示平台关键数据，如用户总数、活跃用户数等\n- 用户管理：查看所有用户信息，支持新增、编辑、删除用户账号' :
                '请描述系统的功能模块，例如：\n\n文化展示模块：\n用户可浏览川剧历史、艺术特点及经典剧目，通过图文与视频了解非遗文化。管理员负责发布、编辑和删除内容。\n\n川剧活动模块：\n用户可查看演出、讲座、体验课等活动详情，并在线提交预约报名。管理员负责发布活动、设置时间地点与人数限制。\n\n周边商品模块：\n用户可浏览并购买脸谱、服饰等川剧文创商品，支付后获得等值积分。管理员负责商品上架、价格库存管理。'"
              clearable>
          </el-input>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showAIDialog = false">取消</el-button>
        <el-button type="primary" @click="submitAIGenerate">生成功能图</el-button>
      </span>
    </el-dialog>

    <!-- 系统项目描述查看弹框 -->
    <el-dialog title="系统项目描述" :visible.sync="showDescDialog" width="500px">
      <div style="white-space: pre-wrap; line-height: 1.8; font-size: 14px; color: #333; height: 300px; overflow-y: auto; padding: 4px 8px;">{{ currentDesc }}</div>
      <span slot="footer">
        <el-button @click="showDescDialog = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 导出对话框 -->
    <ExportDialog
        :visible.sync="isExportDialogVisible"
        @confirm="handleExportConfirm"
    />
  </div>
</template>

<script>
import ExportDialog from '@/components/ExportDialog.vue'

export default {
  name: 'HelloWorld',
  components: {
    ExportDialog
  },
  data() {
    return {
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      nodeIdCounter: 1000,
      selectedNodeId: null,
      svgWidth: 1000,
      svgHeight: 800,
      zoomLevel: 1,
      isFullscreen: false,
      fontFamily: 'SimSun',
      fontSize: '14',
      lineType: 'line',
      rectHeight: 120,
      levelSpacing: 50,
      svgKey: 0,
      showHistoryDialog: false,
      historyArchitectures: [],
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      pointsConfig: {
        structureChart: 3
      },
      // 节点拖拽相关
      draggingNode: null,
      dragStartX: 0,
      dragStartY: 0,
      nodeOffsets: {}, // 存储每个节点的偏移量
      // 画布拖拽相关
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panX: 0,
      panY: 0,
      // 文字编辑相关
      editingNode: null,
      editingText: '',
      editInputStyle: {},
      // 悬停节点
      hoveredNodeId: null,
      hideButtonTimer: null,
      // 绘制模式
      drawMode: 'role',
      // AI生成对话框
      showAIDialog: false,
      aiForm: {
        projectName: '',
        viewType: 'role',
        projectDescription: ''
      },
      // 导出对话框
      isExportDialogVisible: false,
      // 描述查看弹框
      showDescDialog: false,
      currentDesc: '',
      // 角色视图数据
      roleTreeData: [
        {
          id: 0,
          label: '川剧文化传播平台',
          level: 1,
          children: [
            {
              id: 1,
              label: '用户端',
              level: 2,
              children: [
                {id: 11, label: '登录注册', level: 3},
                {id: 12, label: '首页', level: 3},
                {id: 13, label: '川剧介绍', level: 3},
                {id: 14, label: '艺术特点', level: 3},
                {
                  id: 15, label: '川剧活动', level: 3,
                  children: [
                    {id: 151, label: '活动预约', level: 4}
                  ]
                },
                {
                  id: 16, label: '周边商品', level: 3,
                  children: [
                    {id: 161, label: '购买商品', level: 4}
                  ]
                },
                {
                  id: 17, label: '转盘小游戏', level: 3,
                  children: [
                    {id: 171, label: '积分兑换', level: 4}
                  ]
                },
                {id: 18, label: '留言互动', level: 3},
                {
                  id: 19, label: '个人中心', level: 3,
                  children: [
                    {id: 191, label: '个人信息', level: 4},
                    {id: 192, label: '修改密码', level: 4},
                    {id: 193, label: '我的收藏', level: 4},
                    {id: 194, label: '我的订单', level: 4},
                    {id: 195, label: '我的兑换', level: 4}
                  ]
                }
              ]
            },
            {
              id: 2,
              label: '管理端',
              level: 2,
              children: [
                {id: 21, label: '登录', level: 3},
                {id: 22, label: '数据统计', level: 3},
                {id: 23, label: '用户管理', level: 3},
                {id: 24, label: '商品管理', level: 3},
                {id: 25, label: '订单管理', level: 3},
                {id: 26, label: '活动管理', level: 3},
                {id: 27, label: '艺术特点管理', level: 3},
                {id: 28, label: '评论管理', level: 3},
                {id: 29, label: '留言管理', level: 3},
                {id: 210, label: '转盘奖品管理', level: 3},
                {id: 211, label: '轮播图管理', level: 3},
                {id: 212, label: '个人中心', level: 3}
              ]
            }
          ]
        }
      ],
      // 功能视图数据
      functionTreeData: [
        {
          id: 0,
          label: '川剧文化传播平台',
          level: 1,
          children: [
            {
              id: 1,
              label: '文化展示模块',
              level: 2,
              children: [
                {id: 11, label: '川剧历史', level: 3},
                {id: 12, label: '经典剧目', level: 3},
                {id: 13, label: '内容管理', level: 3},
                {id: 14, label: '轮播图管理', level: 3}
              ]
            },
            {
              id: 2,
              label: '川剧活动模块',
              level: 2,
              children: [
                {id: 21, label: '活动浏览', level: 3},
                {id: 22, label: '活动详情', level: 3},
                {id: 23, label: '在线预约', level: 3},
                {id: 24, label: '活动发布', level: 3},
                {id: 25, label: '报名管理', level: 3}
              ]
            },
            {
              id: 3,
              label: '周边商品模块',
              level: 2,
              children: [
                {id: 31, label: '商品浏览', level: 3},
                {id: 32, label: '在线购买', level: 3},
                {id: 33, label: '商品上架', level: 3},
                {id: 34, label: '库存管理', level: 3},
                {id: 35, label: '订单处理', level: 3}
              ]
            },
            {
              id: 4,
              label: '积分转盘模块',
              level: 2,
              children: [
                {id: 41, label: '转盘抽奖', level: 3},
                {id: 42, label: '中奖记录', level: 3},
                {id: 43, label: '奖品配置', level: 3},
                {id: 44, label: '概率设置', level: 3},
                {id: 45, label: '库存管理', level: 3}
              ]
            },
            {
              id: 5,
              label: '社区互动模块',
              level: 2,
              children: [
                {id: 51, label: '留言评论', level: 3},
                {id: 52, label: '内容审核', level: 3},
                {id: 53, label: '评论管理', level: 3}
              ]
            },
            {
              id: 6,
              label: '后台管理模块',
              level: 2,
              children: [
                {id: 61, label: '用户管理', level: 3},
                {id: 62, label: '内容管理', level: 3},
                {id: 63, label: '商品管理', level: 3},
                {id: 65, label: '活动管理', level: 3},
                {id: 66, label: '数据统计', level: 3}
              ]
            }
          ]
        }
      ]
    }
  },
  computed: {
    // 根据绘制模式返回对应的树形数据
    treeData() {
      return this.drawMode === 'role' ? this.roleTreeData : this.functionTreeData;
    },

    allVisibleNodes() {
      // 验证 treeData 是否为数组
      if (!Array.isArray(this.treeData) || this.treeData.length === 0) {
        return [];
      }

      const nodes = [];
      const levelSpacing = this.levelSpacing;
      const minNodeSpacing = 20;
      const startY = 30;

      // 先计算每个级别的最大文字长度
      const getMaxTextLengthForLevel = (nodeList, level) => {
        let maxLength = 0;
        nodeList.forEach(node => {
          if (level > 1) {
            maxLength = Math.max(maxLength, node.label.length);
          }
          if (node.children && node.children.length > 0) {
            maxLength = Math.max(maxLength, getMaxTextLengthForLevel(node.children, level + 1));
          }
        });
        return maxLength;
      };

      // 计算2-4级节点的统一高度，使用用户设置的高度
      const uniformHeight = this.rectHeight;

      // 计算每个节点需要的总宽度（包括所有子孙节点）
      const calculateNodeWidth = (node, level) => {
        let nodeWidth, nodeHeight;

        if (level === 1) {
          const textWidth = node.label.length * parseInt(this.fontSize) * 0.8 + 80;
          nodeWidth = Math.max(120, textWidth);
          nodeHeight = parseInt(this.fontSize) * 1.8 + 15;
        } else {
          nodeWidth = 35;
          nodeHeight = uniformHeight;
        }

        if (node.children && node.children.length > 0) {
          let childrenTotalWidth = 0;
          node.children.forEach(child => {
            childrenTotalWidth += calculateNodeWidth(child, level + 1);
          });
          childrenTotalWidth += (node.children.length - 1) * minNodeSpacing;
          return Math.max(nodeWidth, childrenTotalWidth);
        }

        return nodeWidth;
      };

      // 布局节点
      const layoutNodes = (nodeList, level, startX, parentCenterX = 0) => {
        const levelNodes = [];
        let currentX = startX;

        nodeList.forEach((node, index) => {
          let nodeWidth, nodeHeight;

          if (level === 1) {
            const textWidth = node.label.length * parseInt(this.fontSize) * 0.8 + 80;
            nodeWidth = Math.max(120, textWidth);
            nodeHeight = parseInt(this.fontSize) * 1.8 + 15;
          } else {
            nodeWidth = 35;
            nodeHeight = uniformHeight;
          }

          const totalWidth = calculateNodeWidth(node, level);
          const nodeX = currentX + (totalWidth - nodeWidth) / 2;

          let nodeY;
          if (level === 1) {
            nodeY = startY;
          } else {
            const actualSpacing = this.levelSpacing;
            if (level === 2) {
              const level1Height = parseInt(this.fontSize) * 1.8 + 15;
              nodeY = startY + level1Height + actualSpacing;
            } else {
              const level1Height = parseInt(this.fontSize) * 1.8 + 15;
              const otherLevelHeight = uniformHeight;
              nodeY = startY + level1Height + actualSpacing + (level - 2) * (otherLevelHeight + actualSpacing);
            }
          }

          // 应用节点偏移量（如果有拖拽过）
          const offset = this.nodeOffsets[node.id] || { x: 0, y: 0 };

          const nodeData = {
            ...node,
            x: nodeX + offset.x,
            y: nodeY + offset.y,
            width: nodeWidth,
            height: nodeHeight,
            level
          };

          nodes.push(nodeData);
          levelNodes.push(nodeData);

          if (node.children && node.children.length > 0) {
            const childrenStartX = currentX;
            const childrenTotalWidth = totalWidth;
            layoutNodes(node.children, level + 1, childrenStartX, nodeX + nodeWidth / 2);
          }

          currentX += totalWidth + minNodeSpacing;
        });

        return levelNodes;
      };

      let totalFirstLevelWidth = 0;
      this.treeData.forEach(node => {
        totalFirstLevelWidth += calculateNodeWidth(node, 1);
      });
      totalFirstLevelWidth += (this.treeData.length - 1) * minNodeSpacing;

      const firstLevelStartX = (this.svgWidth - totalFirstLevelWidth) / 2;
      layoutNodes(this.treeData, 1, firstLevelStartX);

      return nodes;
    },

    connectionLines() {
      // 验证 treeData 是否为数组
      if (!Array.isArray(this.treeData) || this.treeData.length === 0) {
        return [];
      }

      const lines = [];
      const nodes = this.allVisibleNodes;

      // 为每个有子节点的节点创建连接线
      const processConnections = (nodeList, level) => {
        nodeList.forEach(node => {
          if (node.children && node.children.length > 0) {
            const parentNode = nodes.find(n => n.id === node.id);

            if (parentNode && node.children.length > 0) {
              // 父节点底部中心点
              const parentCenterX = parentNode.x + parentNode.width / 2;
              const parentBottomY = parentNode.y + parentNode.height;

              // 如果有多个子节点，先画到子节点区域的中间
              const childNodes = node.children.map(child => nodes.find(n => n.id === child.id)).filter(Boolean);

              if (childNodes.length > 0) {
                // 计算子节点的范围
                const leftmostChild = childNodes.reduce((min, child) => child.x < min.x ? child : min);
                const rightmostChild = childNodes.reduce((max, child) => child.x + child.width > max.x + max.width ? child : max);

                const childrenCenterX = (leftmostChild.x + rightmostChild.x + rightmostChild.width) / 2;
                const childrenTopY = childNodes[0].y;

                // 如果只有一个子节点，直接连接
                if (childNodes.length === 1) {
                  const childCenterX = childNodes[0].x + childNodes[0].width / 2;
                  const path = `M ${parentCenterX} ${parentBottomY} L ${childCenterX} ${childrenTopY}`;
                  lines.push({
                    id: `${parentNode.id}-${childNodes[0].id}`,
                    path,
                    isToChild: true
                  });
                } else {
                  // 多个子节点：父节点 -> 水平线 -> 各个子节点
                  const midY = parentBottomY + (childrenTopY - parentBottomY) / 2; // 水平折线在父子节点的中点

                  // 主干线：从父节点到水平分支线
                  const mainPath = `M ${parentCenterX} ${parentBottomY} L ${parentCenterX} ${midY}`;
                  lines.push({
                    id: `main-${parentNode.id}`,
                    path: mainPath,
                    isToChild: false
                  });

                  // 水平分支线
                  const horizontalPath = `M ${leftmostChild.x + leftmostChild.width / 2} ${midY} L ${rightmostChild.x + rightmostChild.width / 2} ${midY}`;
                  lines.push({
                    id: `horizontal-${parentNode.id}`,
                    path: horizontalPath,
                    isToChild: false
                  });

                  // 各个子节点的连接线（只有这些线应该有箭头）
                  childNodes.forEach(childNode => {
                    const childCenterX = childNode.x + childNode.width / 2;
                    const branchPath = `M ${childCenterX} ${midY} L ${childCenterX} ${childrenTopY}`;
                    lines.push({
                      id: `${parentNode.id}-${childNode.id}`,
                      path: branchPath,
                      isToChild: true
                    });
                  });
                }
              }
            }

            // 递归处理子节点
            processConnections(node.children, level + 1);
          }
        });
      };

      processConnections(this.treeData, 1);
      return lines;
    },

    zoomedViewBox() {
      const baseWidth = 1000;
      const baseHeight = 800;
      const scaledWidth = baseWidth / this.zoomLevel;
      const scaledHeight = baseHeight / this.zoomLevel;
      const offsetX = (baseWidth - scaledWidth) / 2 - this.panX;
      const offsetY = (baseHeight - scaledHeight) / 2 - this.panY;
      return `${offsetX} ${offsetY} ${scaledWidth} ${scaledHeight}`;
    }
  },
  methods: {
    // 加载积分配置
    async loadPointsConfig() {
      try {
        const response = await this.request.get('/ai/points-config')
        if (response.code === '200' && response.data) {
          this.pointsConfig = response.data
        }
      } catch (error) {
        console.error('加载积分配置失败:', error)
      }
    },

    handleNodeClick(data) {
      if (!data.editing) {
        console.log('点击节点:', data);
        // 如果点击的是一级节点，同步选中状态
        if (data.level === 1) {
          this.selectedNodeId = data.id;
        }
      }
    },

    startEdit(data) {
      this.$set(data, 'editing', true);
      this.$set(data, 'tempLabel', data.label);
      this.$nextTick(() => {
        // 聚焦到输入框
        const inputs = this.$refs.editInput;
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[inputs.length - 1] : inputs;
          input.focus();
        }
      });
    },
    saveEdit(data) {
      if (data.tempLabel && data.tempLabel.trim()) {
        const newLabel = data.tempLabel.trim();
        // 只有在文字真正发生变化时才显示成功提示
        if (newLabel !== data.label) {
          data.label = newLabel;
          this.$message.success('修改成功');
        }
      }
      this.cancelEdit(data);
    },
    cancelEdit(data) {
      this.$set(data, 'editing', false);
      this.$delete(data, 'tempLabel');
    },
    append(data) {
      const newChild = {
        id: ++this.nodeIdCounter,
        label: `新菜单`,
        level: data.level + 1,
        children: data.level < 3 ? [] : undefined
      };
      if (!data.children) {
        this.$set(data, 'children', []);
      }
      data.children.push(newChild);
      this.$message.success('添加成功');
    },
    remove(node, data) {
      const parent = node.parent;
      const children = parent.data.children || parent.data;
      const index = children.findIndex(d => d.id === data.id);
      children.splice(index, 1);
      this.$message.success('删除成功');
    },
    getLevel1Nodes() {
      // 获取所有一级节点
      return this.treeData;
    },
    selectNode(node) {
      this.selectedNodeId = node.id;
      console.log('选中节点:', node);
    },

    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel * 1.2, 3); // 最大放大3倍
    },

    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel / 1.2, 0.5); // 最小缩小到0.5倍
    },

    downloadImage() {
      const svgElement = document.querySelector('.diagram-svg');

      if (!svgElement) {
        this.$message.error('未找到图表内容');
        return;
      }

      try {
        // 获取所有节点的实际边界
        const allNodes = this.allVisibleNodes;
        if (!allNodes || allNodes.length === 0) {
          this.$message.error('没有可导出的内容');
          return;
        }

        // 计算实际内容的边界
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        allNodes.forEach(node => {
          minX = Math.min(minX, node.x);
          minY = Math.min(minY, node.y);
          maxX = Math.max(maxX, node.x + node.width);
          maxY = Math.max(maxY, node.y + node.height);
        });

        // 添加边距
        const padding = 50;
        minX -= padding;
        minY -= padding;
        maxX += padding;
        maxY += padding;

        // 计算实际需要的尺寸
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;

        // 克隆SVG并设置正确的尺寸
        const svgClone = svgElement.cloneNode(true);
        svgClone.setAttribute('width', contentWidth);
        svgClone.setAttribute('height', contentHeight);
        svgClone.setAttribute('viewBox', `${minX} ${minY} ${contentWidth} ${contentHeight}`);
        svgClone.style.width = contentWidth + 'px';
        svgClone.style.height = contentHeight + 'px';

        // 创建临时容器
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.backgroundColor = 'white';
        tempContainer.appendChild(svgClone);
        document.body.appendChild(tempContainer);

        // 转换为图片
        this.svgToImage(svgClone, contentWidth, contentHeight).then(dataUrl => {
          // 创建下载链接
          const link = document.createElement('a');
          link.download = '框架图.png';
          link.href = dataUrl;
          link.click();

          // 清理
          document.body.removeChild(tempContainer);
          this.$message.success('图片导出成功');

        }).catch(error => {
          console.error('导出失败:', error);
          document.body.removeChild(tempContainer);
          this.$message.error('导出失败，请重试');
        });

      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败，请重试');
      }
    },

    svgToImage(svgElement, width, height) {
      return new Promise((resolve, reject) => {
        // 获取SVG的XML字符串
        let svgData = new XMLSerializer().serializeToString(svgElement);

        // 添加必要的命名空间
        if (!svgData.includes('xmlns=')) {
          svgData = svgData.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        if (!svgData.includes('xmlns:xlink=')) {
          svgData = svgData.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
        }

        // 创建Canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // 使用2倍分辨率以确保清晰度
        const pixelRatio = 2;

        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';

        // 设置高质量渲染
        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 设置白色背景
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        // 创建Image对象
        const img = new Image();

        img.onload = function () {
          try {
            // 绘制SVG到Canvas
            ctx.drawImage(img, 0, 0, width, height);

            // 导出为PNG
            const dataUrl = canvas.toDataURL('image/png', 1.0);
            resolve(dataUrl);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = function (error) {
          reject(new Error('图片加载失败: ' + error));
        };

        // 将SVG转换为Data URI
        const blob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
        const url = URL.createObjectURL(blob);

        img.onload = function () {
          try {
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/png', 1.0);
            URL.revokeObjectURL(url); // 清理URL
            resolve(dataUrl);
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error);
          }
        };

        img.onerror = function () {
          URL.revokeObjectURL(url);
          reject(new Error('图片加载失败'));
        };

        img.src = url;
      });
    },

    toggleFullscreen() {
      const element = document.querySelector('.preview-panel');

      if (!element) {
        this.$message.error('无法进入全屏模式');
        return;
      }

      if (!this.isFullscreen) {
        // 进入全屏
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
        this.isFullscreen = true;
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        this.isFullscreen = false;
      }
    },

    handleFullscreenChange() {
      // 检测是否真的在全屏状态
      this.isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      );
    },

    updateFont() {
      // 强制重新渲染SVG
      this.svgKey++;
      this.$nextTick(() => {
        // 确保DOM更新后再次强制更新
        this.$forceUpdate();
      });
    },

    startDrag(event, node) {
      event.stopPropagation();
      this.draggingNode = node;
      const svg = event.currentTarget.ownerSVGElement;
      const pt = svg.createSVGPoint();
      pt.x = event.clientX;
      pt.y = event.clientY;
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
      this.dragStartX = svgP.x - node.x;
      this.dragStartY = svgP.y - node.y;
    },

    onDrag(event) {
      if (this.draggingNode) {
        // 拖动节点
        event.preventDefault();
        const svg = event.currentTarget;
        const pt = svg.createSVGPoint();
        pt.x = event.clientX;
        pt.y = event.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

        const newX = svgP.x - this.dragStartX;
        const newY = svgP.y - this.dragStartY;

        // 计算偏移量（相对于原始位置）
        const originalNode = this.allVisibleNodes.find(n => n.id === this.draggingNode.id);
        if (originalNode) {
          const offsetX = newX - (originalNode.x - (this.nodeOffsets[this.draggingNode.id]?.x || 0));
          const offsetY = newY - (originalNode.y - (this.nodeOffsets[this.draggingNode.id]?.y || 0));

          this.$set(this.nodeOffsets, this.draggingNode.id, { x: offsetX, y: offsetY });
        }
      } else if (this.isPanning) {
        // 拖动画布（修正方向）
        const dx = (event.clientX - this.panStartX) / this.zoomLevel;
        const dy = (event.clientY - this.panStartY) / this.zoomLevel;
        this.panX += dx;
        this.panY += dy;
        this.panStartX = event.clientX;
        this.panStartY = event.clientY;
      }
    },

    endDrag() {
      this.draggingNode = null;
      this.isPanning = false;
    },

    startPan(event) {
      // 只有点击空白区域才开始拖动画布
      if (event.target.tagName === 'svg' || event.target.classList.contains('connections')) {
        this.isPanning = true;
        this.panStartX = event.clientX;
        this.panStartY = event.clientY;
      }
    },

    startEditNode(event, node) {
      event.stopPropagation();
      event.preventDefault();

      this.editingNode = node;
      this.editingText = node.label;

      // 获取 diagram-content 容器
      const container = this.$el.querySelector('.diagram-content');
      const containerRect = container.getBoundingClientRect();

      // 获取 SVG 元素
      const svg = event.currentTarget.ownerSVGElement;
      const svgRect = svg.getBoundingClientRect();

      // 计算节点中心在屏幕上的位置
      const pt = svg.createSVGPoint();
      pt.x = node.x + node.width / 2;
      pt.y = node.y + node.height / 2;
      const screenPt = pt.matrixTransform(svg.getScreenCTM());

      // 相对于 diagram-content 容器的位置
      const left = screenPt.x - containerRect.left;
      const top = screenPt.y - containerRect.top;

      this.editInputStyle = {
        position: 'absolute',
        left: left + 'px',
        top: top + 'px',
        transform: 'translate(-50%, -50%)',
        width: Math.max(node.width * 1.2, 100) + 'px',
        minHeight: '30px',
        fontSize: this.fontSize + 'px',
        fontFamily: this.fontFamily,
        textAlign: 'center',
        zIndex: 1000
      };

      this.$nextTick(() => {
        const input = this.$refs.editInput;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    saveEditNode() {
      if (!this.editingNode) {
        return;
      }

      const newText = this.editingText.trim();

      // 如果文字没有改变，不提示
      if (!newText || newText === this.editingNode.label) {
        this.editingNode = null;
        this.editingText = '';
        return;
      }

      // 更新树形数据中的对应节点
      const updateTreeNode = (nodes) => {
        for (let node of nodes) {
          if (node.id === this.editingNode.id) {
            node.label = newText;
            return true;
          }
          if (node.children && node.children.length > 0) {
            if (updateTreeNode(node.children)) {
              return true;
            }
          }
        }
        return false;
      };

      const updated = updateTreeNode(this.treeData);

      if (updated) {
        this.$message.success('修改成功');
      }

      this.editingNode = null;
      this.editingText = '';
    },

    cancelEditNode() {
      this.editingNode = null;
      this.editingText = '';
    },

    saveToLocalStorage() {
      try {
        console.log('========== 开始保存功能 ==========');
        console.log('当前绘制模式:', this.drawMode);

        // 先深拷贝当前的树形数据，避免引用问题
        const currentTreeData = JSON.parse(JSON.stringify(this.treeData));
        console.log('深拷贝后的树形数据:', currentTreeData);
        console.log('树形数据的内存地址（用于调试）:', currentTreeData);

        // 获取已保存的功能列表
        let savedArchitectures = [];
        const existingData = localStorage.getItem('savedArchitectures');

        if (existingData) {
          savedArchitectures = JSON.parse(existingData);
          console.log('现有功能数量:', savedArchitectures.length);
          console.log('现有功能列表:', savedArchitectures.map(a => ({
            id: a.id,
            name: a.name,
            projectName: a.projectName,
            nodeCount: a.nodeCount
          })));
        }

        // 创建新的功能记录
        const timestamp = new Date();

        // 计算节点数
        const nodeCount = this.countNodes(currentTreeData);

        // 获取课题名称（从树的根节点）
        const projectName = currentTreeData && currentTreeData.length > 0
          ? currentTreeData[0].label
          : '未命名项目';

        const newArchitecture = {
          id: timestamp.getTime(),
          name: `功能_${timestamp.toLocaleString()}`,
          projectName: projectName,
          viewType: this.drawMode,
          viewTypeName: this.drawMode === 'role' ? '角色视图' : '功能视图',
          projectDescription: `手动保存的${this.drawMode === 'role' ? '角色视图' : '功能视图'}功能图`,
          nodeCount: nodeCount,
          data: currentTreeData, // 已经是深拷贝的数据
          drawMode: this.drawMode,
          saveTime: timestamp.toISOString(),
          createTime: timestamp.toLocaleString()
        };

        console.log('新创建的功能记录:', {
          id: newArchitecture.id,
          name: newArchitecture.name,
          projectName: newArchitecture.projectName,
          viewType: newArchitecture.viewType,
          nodeCount: newArchitecture.nodeCount
        });

        // 将新功能添加到数组中
        savedArchitectures.push(newArchitecture);
        console.log('添加后的功能数量:', savedArchitectures.length);

        // 限制最多保存20条功能，超过则删除最旧的
        const maxArchitectures = 20;
        if (savedArchitectures.length > maxArchitectures) {
          const removedCount = savedArchitectures.length - maxArchitectures;
          savedArchitectures.splice(0, removedCount);
          console.log(`删除了 ${removedCount} 条最旧的功能`);
        }

        // 保存到localStorage前，再次验证每条记录的独立性
        console.log('保存前验证 - 所有功能的课题名称:', savedArchitectures.map(a => a.projectName));

        // 保存到localStorage
        const dataToSave = JSON.stringify(savedArchitectures);
        localStorage.setItem('savedArchitectures', dataToSave);

        // 验证保存是否成功
        const verifyData = localStorage.getItem('savedArchitectures');
        const verifyList = JSON.parse(verifyData);
        console.log('保存后验证 - 所有功能的课题名称:', verifyList.map(a => a.projectName));
        console.log('========== 保存完成 ==========');

        // 显示成功消息
        this.$message.success(`功能已保存！当前共有 ${savedArchitectures.length} 个已保存的功能`);

      } catch (error) {
        console.error('保存功能失败:', error);
        this.$message.error('保存功能失败，请重试');
      }
    },

    generateAIDiagram() {
      this.showAIDialog = true;
    },

    // 提交AI生成
    async submitAIGenerate() {
      // 验证表单
      if (!this.aiForm.projectName.trim()) {
        this.$message.warning('请输入课题名称');
        return;
      }

      if (!this.aiForm.projectDescription.trim()) {
        this.$message.warning('请输入系统项目描述');
        return;
      }

      // 添加积分确认
      try {
        await this.$confirm(`此操作将消耗${this.pointsConfig.structureChart}积分，是否继续？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
      } catch {
        this.$message.info('已取消操作');
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: 'AI正在生成功能图，请稍候...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        // 调用后端AI接口生成功能图
        const response = await this.request.post('/ai/generateStructure', {
          projectName: this.aiForm.projectName,
          viewType: this.aiForm.viewType,
          projectDescription: this.aiForm.projectDescription
        });

        loading.close();

        if (response.code === 200 || response.code === '200') {
          // 验证返回的数据格式
          if (!response.data || !Array.isArray(response.data)) {
            this.$message.error('AI返回的数据格式不正确');
            return;
          }

          // 根据视图类型更新对应的数据
          if (this.aiForm.viewType === 'role') {
            this.roleTreeData = response.data;
          } else {
            this.functionTreeData = response.data;
          }

          // 切换到对应的视图
          this.drawMode = this.aiForm.viewType;

          // 自动保存到历史记录
          this.saveAIGeneratedToHistory(response.data);

          this.$message.success('AI功能图生成成功');

          // 刷新积分
          this.refreshPoints();

          // 关闭对话框
          this.showAIDialog = false;

          // 重置表单
          this.aiForm = {
            projectName: '',
            viewType: 'role',
            projectDescription: ''
          };
        } else {
          this.$message.error('生成失败：' + (response.msg || '未知错误'));
        }
      } catch (error) {
        loading.close();
        console.error('AI生成失败：', error);
        this.$message.error('生成失败：' + (error.message || '未知错误'));
      }
    },

    // 保存AI生成的结果到历史记录
    saveAIGeneratedToHistory(treeData) {
      try {
        // 验证数据格式
        if (!treeData || !Array.isArray(treeData)) {
          console.error('无法保存历史记录：数据格式不正确');
          return;
        }

        // 获取已保存的功能列表
        let savedArchitectures = [];
        const existingData = localStorage.getItem('savedArchitectures');

        if (existingData) {
          savedArchitectures = JSON.parse(existingData);
        }

        // 创建新的功能记录
        const timestamp = new Date();
        const nodeCount = this.countNodes(treeData);

        const newArchitecture = {
          id: timestamp.getTime(),
          name: `AI生成_${timestamp.toLocaleString()}`,
          projectName: this.aiForm.projectName,
          viewType: this.aiForm.viewType,
          viewTypeName: this.aiForm.viewType === 'role' ? '角色视图' : '功能视图',
          projectDescription: this.aiForm.projectDescription,
          nodeCount: nodeCount,
          data: JSON.parse(JSON.stringify(treeData)),
          drawMode: this.aiForm.viewType,
          saveTime: timestamp.toISOString(),
          createTime: timestamp.toLocaleString()
        };

        // 将新功能添加到数组中
        savedArchitectures.push(newArchitecture);

        // 限制最多保存20条
        const maxArchitectures = 20;
        if (savedArchitectures.length > maxArchitectures) {
          savedArchitectures.splice(0, savedArchitectures.length - maxArchitectures);
        }

        // 保存到localStorage
        localStorage.setItem('savedArchitectures', JSON.stringify(savedArchitectures));

        console.log('AI生成结果已自动保存到历史记录');
      } catch (error) {
        console.error('保存AI生成结果失败:', error);
      }
    },

    loadFromLocalStorage() {
      try {
        // 从localStorage获取保存的功能列表
        const savedArchitectures = localStorage.getItem('savedArchitectures');

        if (savedArchitectures) {
          const architecturesList = JSON.parse(savedArchitectures);

          // 验证数据格式
          if (Array.isArray(architecturesList) && architecturesList.length > 0) {
            // 获取最后一个保存的功能（最新的）
            const latestArchitecture = architecturesList[architecturesList.length - 1];

            // 验证功能数据完整性
            if (latestArchitecture && latestArchitecture.data && Array.isArray(latestArchitecture.data)) {
              // 加载最新的功能数据到组件
              if (latestArchitecture.drawMode === 'role') {
                this.roleTreeData = JSON.parse(JSON.stringify(latestArchitecture.data));
              } else {
                this.functionTreeData = JSON.parse(JSON.stringify(latestArchitecture.data));
              }

              // 切换到对应的绘制模式
              this.drawMode = latestArchitecture.drawMode || 'role';
              return;
            }
          }
        }

      } catch (error) {
        console.error('加载功能数据失败:', error);
        this.$message.warning('加载保存的功能失败，使用默认数据');
      }
    },

    showSavedArchitectures() {
      try {
        const savedArchitectures = localStorage.getItem('savedArchitectures');

        if (!savedArchitectures) {
          this.historyArchitectures = [];
          this.totalRecords = 0;
          this.showHistoryDialog = true;
          return;
        }

        const architecturesList = JSON.parse(savedArchitectures);

        if (!Array.isArray(architecturesList) || architecturesList.length === 0) {
          this.historyArchitectures = [];
          this.totalRecords = 0;
          this.showHistoryDialog = true;
          return;
        }

        // 按时间倒序排序（最新的在前面）
        const sortedArchitectures = architecturesList.sort((a, b) => b.id - a.id);

        // 设置总记录数
        this.totalRecords = sortedArchitectures.length;

        // 分页处理
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        const pagedArchitectures = sortedArchitectures.slice(startIndex, endIndex);

        // 处理数据用于表格显示，确保所有字段都存在，并深拷贝数据避免引用问题
        this.historyArchitectures = pagedArchitectures.map((architecture, index) => {
          // 深拷贝整个对象，避免引用问题
          const deepCopiedArchitecture = JSON.parse(JSON.stringify(architecture));
          return {
            ...deepCopiedArchitecture,
            index: startIndex + index + 1,
            nodeCount: deepCopiedArchitecture.nodeCount || this.countNodes(deepCopiedArchitecture.data),
            projectName: deepCopiedArchitecture.projectName || '未命名项目',
            viewTypeName: deepCopiedArchitecture.viewTypeName || (deepCopiedArchitecture.viewType === 'role' ? '角色视图' : '功能视图'),
            projectDescription: deepCopiedArchitecture.projectDescription || ''
          };
        });

        // 显示对话框
        this.showHistoryDialog = true;

      } catch (error) {
        console.error('获取功能列表失败:', error);
        this.$message.error('获取功能列表失败');
        this.historyArchitectures = [];
        this.totalRecords = 0;
        this.showHistoryDialog = true;
      }
    },

    loadHistoryArchitecture(architecture) {
      try {
        // 验证数据格式
        if (!architecture.data || !Array.isArray(architecture.data)) {
          this.$message.error('历史数据格式不正确');
          return;
        }

        // 加载选中的功能数据
        if (architecture.drawMode === 'role') {
          this.roleTreeData = JSON.parse(JSON.stringify(architecture.data));
        } else {
          this.functionTreeData = JSON.parse(JSON.stringify(architecture.data));
        }

        // 切换到对应的绘制模式
        this.drawMode = architecture.drawMode || 'role';

        // 关闭对话框
        this.showHistoryDialog = false;

        // 显示成功消息
        this.$message.success(`已加载功能：${architecture.name}`);

      } catch (error) {
        console.error('加载功能失败:', error);
        this.$message.error('加载功能失败，请重试');
      }
    },

    // 删除历史功能
    deleteHistoryArchitecture(architecture) {
      this.$confirm('确定要删除这条历史功能吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        try {
          // 从localStorage获取数据
          const savedArchitectures = localStorage.getItem('savedArchitectures');
          if (!savedArchitectures) {
            this.$message.error('未找到历史数据');
            return;
          }

          let architecturesList = JSON.parse(savedArchitectures);

          // 根据id删除对应的记录
          architecturesList = architecturesList.filter(item => item.id !== architecture.id);

          // 保存回localStorage
          localStorage.setItem('savedArchitectures', JSON.stringify(architecturesList));

          // 刷新列表
          this.showSavedArchitectures();

          this.$message.success('删除成功');
        } catch (error) {
          console.error('删除失败:', error);
          this.$message.error('删除失败，请重试');
        }
      }).catch(() => {});
    },

    // 辅助方法：计算树形数据的节点总数
    countNodes(treeData) {
      let count = 0;
      const countRecursive = (nodes) => {
        if (Array.isArray(nodes)) {
          nodes.forEach(node => {
            count++;
            if (node.children && Array.isArray(node.children)) {
              countRecursive(node.children);
            }
          });
        }
      };
      countRecursive(treeData);
      return count;
    },

    // 分页切换
    handlePageChange(page) {
      this.currentPage = page;
      this.showSavedArchitectures();
    },

    // 处理节点鼠标悬停
    handleNodeMouseOver(node) {
      if (this.hideButtonTimer) {
        clearTimeout(this.hideButtonTimer);
        this.hideButtonTimer = null;
      }
      this.hoveredNodeId = node.id;
    },

    // 处理按钮鼠标悬停
    handleButtonMouseOver(node) {
      if (this.hideButtonTimer) {
        clearTimeout(this.hideButtonTimer);
        this.hideButtonTimer = null;
      }
      this.hoveredNodeId = node.id;
    },

    // 处理鼠标移出
    handleNodeMouseOut() {
      // 延迟200ms隐藏，给用户时间移动到按钮上
      this.hideButtonTimer = setTimeout(() => {
        this.hoveredNodeId = null;
      }, 200);
    },

    // 添加兄弟节点（左侧或右侧）
    addSiblingNode(node, position) {
      // 在树形数据中找到该节点的父节点
      const findParentAndAdd = (nodes, targetId, parentNode = null) => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === targetId) {
            // 找到目标节点，在其父节点的children中添加
            if (parentNode && parentNode.children) {
              const targetIndex = parentNode.children.findIndex(n => n.id === targetId);
              const newNode = {
                id: ++this.nodeIdCounter,
                label: '新菜单',
                level: node.level,
                children: []
              };

              if (position === 'left') {
                // 插入到目标节点左侧
                parentNode.children.splice(targetIndex, 0, newNode);
              } else {
                // 插入到目标节点右侧
                parentNode.children.splice(targetIndex + 1, 0, newNode);
              }

              this.$message.success(`已在${position === 'left' ? '左侧' : '右侧'}添加新节点`);
              return true;
            }
            return false;
          }

          if (nodes[i].children && nodes[i].children.length > 0) {
            if (findParentAndAdd(nodes[i].children, targetId, nodes[i])) {
              return true;
            }
          }
        }
        return false;
      };

      findParentAndAdd(this.treeData, node.id);
    },

    // 添加子节点（底部）
    addChildNode(node) {
      // 在树形数据中找到该节点并添加子节点
      const findAndAddChild = (nodes) => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === node.id) {
            if (!nodes[i].children) {
              this.$set(nodes[i], 'children', []);
            }

            const newChild = {
              id: ++this.nodeIdCounter,
              label: '新菜单',
              level: node.level + 1,
              children: node.level < 3 ? [] : undefined
            };

            nodes[i].children.push(newChild);
            this.$message.success('已添加子节点');
            return true;
          }

          if (nodes[i].children && nodes[i].children.length > 0) {
            if (findAndAddChild(nodes[i].children)) {
              return true;
            }
          }
        }
        return false;
      };

      findAndAddChild(this.treeData);
    },

    // 从SVG删除节点
    deleteNodeFromSVG(node) {
      this.$confirm('确定要删除该节点吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 在树形数据中找到并删除该节点
        const findAndDelete = (nodes, parentNode = null) => {
          for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].id === node.id) {
              // 找到目标节点
              if (parentNode && parentNode.children) {
                const index = parentNode.children.findIndex(n => n.id === node.id);
                if (index > -1) {
                  parentNode.children.splice(index, 1);
                  this.$message.success('删除成功');
                  this.hoveredNodeId = null;
                  return true;
                }
              }
              return false;
            }

            if (nodes[i].children && nodes[i].children.length > 0) {
              if (findAndDelete(nodes[i].children, nodes[i])) {
                return true;
              }
            }
          }
          return false;
        };

        findAndDelete(this.treeData);
      }).catch(() => {});
    },

    // 查看项目描述
    viewProjectDesc(desc) {
      this.currentDesc = desc;
      this.showDescDialog = true;
    },

    // 打开导出对话框
    openExportDialog() {      if (!this.allVisibleNodes || this.allVisibleNodes.length === 0) {
        this.$message.warning('没有可导出的内容');
        return;
      }
      this.isExportDialogVisible = true;
    },

    // 处理导出确认
    handleExportConfirm(options) {
      if (options.format === 'drawio') {
        this.exportToDrawio();
      } else {
        this.downloadImage();
      }
    },

    // 导出为 Draw.io 格式
    exportToDrawio() {
      try {
        const cells = [];
        const allNodes = this.allVisibleNodes;

        if (!allNodes || allNodes.length === 0) {
          this.$message.error('没有可导出的内容');
          return;
        }

        // 创建一个 ID 映射，确保每个节点都有唯一的 ID
        const nodeIdMap = new Map();
        let uniqueIdCounter = 1;

        allNodes.forEach(node => {
          if (!nodeIdMap.has(node.id)) {
            nodeIdMap.set(node.id, `node-${uniqueIdCounter++}`);
          }
        });

        // 生成节点
        allNodes.forEach(node => {
          const x = Math.round(node.x);
          const y = Math.round(node.y);
          const width = Math.round(node.width);
          const height = Math.round(node.height);

          // 根据层级设置不同的样式
          let style = 'whiteSpace=wrap;html=1;fillColor=white;strokeColor=#000000;';

          if (node.level === 1) {
            // 第1级：水平文字，加粗
            style += 'fontStyle=1;fontSize=14;';
          } else {
            // 2-4级：竖直文字
            style += 'fontSize=14;';
          }

          // 处理竖直文字：将文字用换行符分隔
          let displayText = node.label;
          if (node.level > 1) {
            displayText = node.label.split('').join('<br>');
          }

          const uniqueId = nodeIdMap.get(node.id);

          cells.push(
            `<mxCell id="${uniqueId}" value="${this.escapeXml(displayText)}" vertex="1" parent="1" style="${style}">` +
            `<mxGeometry x="${x}" y="${y}" width="${width}" height="${height}" as="geometry"/>` +
            `</mxCell>`
          );
        });

        // 生成连接线 - 使用树形结构并添加路径点
        let linkCounter = 1;

        const generateLinks = (nodes) => {
          nodes.forEach(node => {
            if (node.children && node.children.length > 0) {
              const parentNode = allNodes.find(n => n.id === node.id);
              if (!parentNode) return;

              const parentId = nodeIdMap.get(node.id);
              const parentCenterX = parentNode.x + parentNode.width / 2;
              const parentBottomY = parentNode.y + parentNode.height;

              if (node.children.length === 1) {
                // 只有一个子节点：直接连接
                const child = node.children[0];
                const childNode = allNodes.find(n => n.id === child.id);
                if (childNode) {
                  const childId = nodeIdMap.get(child.id);
                  const linkId = `link-${linkCounter++}`;

                  let linkStyle = 'endArrow=none;html=1;rounded=0;strokeColor=#000000;exitX=0.5;exitY=1;exitDx=0;exitDy=0;entryX=0.5;entryY=0;entryDx=0;entryDy=0;';

                  if (this.lineType === 'arrow') {
                    linkStyle = 'endArrow=classic;html=1;rounded=0;strokeColor=#000000;exitX=0.5;exitY=1;exitDx=0;exitDy=0;entryX=0.5;entryY=0;entryDx=0;entryDy=0;';
                  }

                  cells.push(
                    `<mxCell id="${linkId}" value="" edge="1" parent="1" source="${parentId}" target="${childId}" style="${linkStyle}">` +
                    `<mxGeometry relative="1" as="geometry"/>` +
                    `</mxCell>`
                  );
                }
              } else {
                // 多个子节点：使用水平分支线
                const childNodes = node.children.map(child => allNodes.find(n => n.id === child.id)).filter(Boolean);

                if (childNodes.length > 0) {
                  const leftmostChild = childNodes.reduce((min, child) => child.x < min.x ? child : min);
                  const rightmostChild = childNodes.reduce((max, child) => child.x + child.width > max.x + max.width ? child : max);

                  const childrenTopY = childNodes[0].y;
                  const midY = parentBottomY + (childrenTopY - parentBottomY) / 2;

                  // 主干线：从父节点到水平分支线的中点
                  const mainLinkId = `link-${linkCounter++}`;
                  let mainStyle = 'endArrow=none;html=1;rounded=0;strokeColor=#000000;exitX=0.5;exitY=1;exitDx=0;exitDy=0;';

                  const mainExitX = Math.round(parentCenterX);
                  const mainExitY = Math.round(parentBottomY);
                  const mainEntryX = Math.round(parentCenterX);
                  const mainEntryY = Math.round(midY);

                  cells.push(
                    `<mxCell id="${mainLinkId}" value="" edge="1" parent="1" source="${parentId}" style="${mainStyle}">` +
                    `<mxGeometry relative="1" as="geometry">` +
                    `<mxPoint x="${mainEntryX}" y="${mainEntryY}" as="targetPoint"/>` +
                    `</mxGeometry>` +
                    `</mxCell>`
                  );

                  // 水平分支线
                  const hLineId = `link-${linkCounter++}`;
                  const leftX = Math.round(leftmostChild.x + leftmostChild.width / 2);
                  const rightX = Math.round(rightmostChild.x + rightmostChild.width / 2);

                  cells.push(
                    `<mxCell id="${hLineId}" value="" edge="1" parent="1" style="endArrow=none;html=1;rounded=0;strokeColor=#000000;">` +
                    `<mxGeometry relative="1" as="geometry">` +
                    `<mxPoint x="${leftX}" y="${mainEntryY}" as="sourcePoint"/>` +
                    `<mxPoint x="${rightX}" y="${mainEntryY}" as="targetPoint"/>` +
                    `</mxGeometry>` +
                    `</mxCell>`
                  );

                  // 各个子节点的连接线
                  childNodes.forEach(childNode => {
                    const child = node.children.find(c => c.id === childNode.id);
                    if (child) {
                      const childId = nodeIdMap.get(child.id);
                      const childCenterX = Math.round(childNode.x + childNode.width / 2);
                      const childTopY = Math.round(childNode.y);
                      const branchLinkId = `link-${linkCounter++}`;

                      let branchStyle = 'endArrow=none;html=1;rounded=0;strokeColor=#000000;entryX=0.5;entryY=0;entryDx=0;entryDy=0;';

                      if (this.lineType === 'arrow') {
                        branchStyle = 'endArrow=classic;html=1;rounded=0;strokeColor=#000000;entryX=0.5;entryY=0;entryDx=0;entryDy=0;';
                      }

                      cells.push(
                        `<mxCell id="${branchLinkId}" value="" edge="1" parent="1" target="${childId}" style="${branchStyle}">` +
                        `<mxGeometry relative="1" as="geometry">` +
                        `<mxPoint x="${childCenterX}" y="${mainEntryY}" as="sourcePoint"/>` +
                        `</mxGeometry>` +
                        `</mxCell>`
                      );
                    }
                  });
                }
              }

              // 递归处理子节点
              node.children.forEach(child => {
                if (child.children && child.children.length > 0) {
                  generateLinks([child]);
                }
              });
            }
          });
        };

        // 从根节点开始生成连接线
        generateLinks(this.treeData);

        // 生成时间戳
        const now = new Date();
        const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`;

        // 获取项目名称
        const projectName = this.treeData && this.treeData.length > 0
          ? this.treeData[0].label
          : '功能图';

        // 生成 XML
        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<mxfile host="app.diagrams.net" modified="${new Date().toISOString()}" agent="AI Generated" version="21.0.0">
  <diagram id="structure-diagram" name="${this.escapeXml(projectName)}">
    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="827" pageHeight="1169" math="0" shadow="0">
      <root>
        <mxCell id="0"/>
        <mxCell id="1" parent="0"/>
        ${cells.join('\n        ')}
      </root>
    </mxGraphModel>
  </diagram>
</mxfile>`;

        // 创建下载
        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.download = `${ts}_${projectName}_功能图.drawio`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        this.$message.success('导出成功！可以使用 Draw.io 打开编辑');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败，请重试');
      }
    },

    // XML 转义
    escapeXml(text) {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
    },

    // 页面访问统计
    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'structureChart' }
      }).catch(() => {});
    },

    // 刷新积分
    refreshPoints() {
      // 使用全局方法刷新积分
      this.$refreshPoints()
    }
  },

  mounted() {
    // 监听全屏状态变化
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange);

    // 从localStorage加载保存的数据
    console.log('页面加载完成，开始加载保存的功能...');
    this.loadFromLocalStorage();
    this.increasePageView();
    this.loadPointsConfig();
  },

  beforeDestroy() {
    // 移除全屏监听
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange);
  }
}
</script>

<style scoped>
.structure-page {
  display: flex;
  height: calc(100vh - 118px);
  overflow: hidden;
}

.editor-panel {
  width: 380px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
  border-right: 1px solid #d1d5db;
}

.panel-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.panel-icon {
  width: 22px;
  height: 22px;
  color: #4f46e5;
}

.btn-history {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #4f46e5;
  background: #fff;
  border: 1px solid #4f46e5;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-history:hover {
  background: #4f46e5;
  color: #fff;
}

.btn-history svg {
  width: 16px;
  height: 16px;
}

.save-reminder {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  margin: 12px 16px 0;
  flex-shrink: 0;
}

.draw-mode-tabs {
  display: flex;
  padding: 0 16px;
  margin-top: 12px;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
}

.draw-mode-tabs .tab-item {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 15px;
  color: #6b7280;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  font-weight: 500;
}

.draw-mode-tabs .tab-item:hover {
  color: #4f46e5;
}

.draw-mode-tabs .tab-item.active {
  color: #4f46e5;
  font-weight: 600;
}

.draw-mode-tabs .tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #4f46e5;
  border-radius: 3px 3px 0 0;
}

.tree-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
}

.panel-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.btn-primary, .btn-secondary, .btn-ai {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-points {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
}

.btn-primary {
  background: #4f46e5;
  color: #fff;
}

.btn-primary:hover {
  background: #4338ca;
}

.btn-secondary {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.btn-ai {
  background: #10b981;
  color: #fff;
}

.btn-ai:hover {
  background: #059669;
}

.btn-primary svg, .btn-secondary svg, .btn-ai svg {
  width: 22px;
  height: 22px;
}

.btn-ai .ai-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.preview-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
  margin-left: 10px;
  border-left: 1px solid #d1d5db;

}

.tabs-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.tabs {
  display: flex;
  gap: 24px;
}

.tab {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  font-size: 16px;
  font-weight: 400;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.tab svg {
  width: 22px;
  height: 22px;
}

.tab:hover {
  color: #374151;
}

.tab.active {
  color: #4f46e5;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #4f46e5;
}

.preview-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-area {
  flex: 1;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.toolbar-buttons {
  display: flex;
  gap: 10px;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tool-btn i {
  font-size: 14px;
}

.btn-purple {
  background: #8c6ff7;
}

.btn-purple:hover {
  background: #7559d4;
}

.btn-green {
  background: #10b981;
}

.btn-green:hover {
  background: #059669;
}

.btn-orange {
  background: #f59e0b;
}

.btn-orange:hover {
  background: #d97706;
}

.btn-blue {
  background: #409eff;
}

.btn-blue:hover {
  background: #2b88e6;
}

.tip-banner {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  flex-shrink: 0;
}

.params-setting {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px 24px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.param-item label {
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
  font-weight: 500;
}

.diagram-content {
  flex: 1;
  overflow: hidden;
  background: white;
  padding: 24px;
  position: relative;
  min-height: calc(100vh - 118px - 60px);
}

.node-edit-input {
  border: 2px solid #4f46e5;
  border-radius: 4px;
  padding: 4px 8px;
  outline: none;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.diagram-svg {
  width: 100%;
  height: 100%;
  max-width: 100%;
  cursor: grab;
}

.diagram-svg.panning {
  cursor: grabbing;
}

.draggable-node {
  cursor: move;
}

.draggable-node:hover {
  stroke: #4f46e5;
  stroke-width: 2;
}

.nodes text {
  pointer-events: none;
}

.connections {
  pointer-events: none;
}

.add-button {
  cursor: pointer;
  pointer-events: all;
}

.add-button:hover .add-btn-circle {
  fill: #059669;
}

.add-btn-circle {
  transition: fill 0.2s;
}

.delete-button {
  cursor: pointer;
  pointer-events: all;
}

.delete-button:hover .delete-btn-circle {
  fill: #dc2626;
}

.delete-btn-circle {
  transition: fill 0.2s;
}

.custom-tree {
  background-color: transparent;
  border: none;
  padding: 0;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.node-actions {
  display: inline-block;
  opacity: 0;
  transition: opacity 0.2s;
}

.custom-tree-node:hover .node-actions {
  opacity: 1;
}

.edit-input {
  width: 180px;
  margin-right: 10px;
}

.edit-input .el-input__inner {
  height: 28px;
  line-height: 28px;
  font-size: 13px;
  border-radius: 4px;
}

.node-actions .el-button {
  margin-left: 4px;
  padding: 4px 6px;
  border-radius: 4px;
}

.node-actions .el-button i {
  font-size: 13px;
}

.custom-tree-node span:first-child {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  flex: 1;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-tree-node span:first-child:hover {
  background-color: #eef2ff;
}

::v-deep .el-tree-node__content {
  margin: 3px 0;
  width: 100%;
  padding: 4px 0;
  border-radius: 6px;
  transition: all 0.2s;
}

::v-deep .el-tree-node__label {
  display: inline-block;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.el-tree-node__content:hover {
  background-color: #f9fafb;
}

.el-tree-node[data-level="1"] > .el-tree-node__content {
  font-weight: 600;
  font-size: 15px;
  color: #4f46e5;
}

.el-tree-node[data-level="2"] > .el-tree-node__content {
  font-size: 14px;
  color: #374151;
}

.el-tree-node[data-level="3"] > .el-tree-node__content {
  font-size: 13px;
  color: #6b7280;
}

.el-tree-node[data-level="4"] > .el-tree-node__content {
  font-size: 13px;
  color: #9ca3af;
}

.el-tree-node__expand-icon {
  display: none !important;
}

.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>
